import React, { useState, useEffect } from "react";
import { useCollection } from "../../hooks/useCollection";
import { useUserRole } from "../../hooks/useUserRole";
import { useNavigate } from "react-router-dom";
import { functions, autenticazione } from "../../firebase/config";
import { httpsCallable } from "firebase/functions";
import "./GestioneRuoli.css";

// Funzione di utilità per normalizzare i dati utente
const normalizeUserData = (user) => {
  if (!user) return null;

  // Estrai i dati dall'oggetto anagrafica se esiste
  const nome = user.anagrafica?.nome || user.nome || "N/A";
  const cognome = user.anagrafica?.cognome || user.cognome || "";
  const email =
    user.anagrafica?.emailGoogle || user.email || "Email non disponibile";

  // Determina i ruoli in base ai custom claims
  const admin = user.customClaims?.admin === true;
  const resTecnico = user.customClaims?.resTecnico === true;

  // Deriva il role se non è presente
  let role = user.role;
  if (!role) {
    if (admin) {
      role = "admin";
    } else if (resTecnico) {
      role = "resTecnico";
    } else {
      role = "basic";
    }
  }

  return {
    ...user,
    nome,
    cognome,
    email,
    role,
    admin,
    resTecnico,
    displayName: `${nome} ${cognome}`.trim() || user.displayName || "Utente",
  };
};

const GestioneRuoli = () => {
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const { documents: users } = useCollection("users");

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Stato per la creazione di un nuovo utente
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserDisplayName, setNewUserDisplayName] = useState("");
  const [newUserRole, setNewUserRole] = useState("basic");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // Aggiungi un effetto per forzare il refresh del token
  useEffect(() => {
    const refreshToken = async () => {
      if (autenticazione.currentUser) {
        try {
          await autenticazione.currentUser.getIdToken(true);
          console.log("Token aggiornato");
        } catch (error) {
          console.error("Errore durante il refresh del token:", error);
        }
      }
    };

    refreshToken();
  }, []);

  // Filtra gli utenti in base al termine di ricerca
  useEffect(() => {
    if (users) {
      // Normalizza i dati degli utenti prima di filtrarli
      const normalizedUsers = users.map(normalizeUserData).filter(Boolean);

      const filtered = normalizedUsers.filter((user) => {
        const fullName = user.displayName.toLowerCase();
        const emailLower = user.email.toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return (
          fullName.includes(searchLower) || emailLower.includes(searchLower)
        );
      });

      setFilteredUsers(filtered);
    }
  }, [users, searchTerm]);

  // Gestisce la selezione di un utente
  const handleUserSelect = (user) => {
    setSelectedUser(normalizeUserData(user));
    setSelectedRole("");
    setError(null);
    setSuccess(null);
  };

  // Gestisce il cambio di ruolo
  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
    setError(null);
    setSuccess(null);
  };

  // Gestisce l'assegnazione del ruolo
  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) {
      setError("Seleziona un utente e un ruolo");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Chiamata alla Cloud Function per impostare il ruolo
      const setUserRole = httpsCallable(functions, "setUserRole");

      const result = await setUserRole({
        uid: selectedUser.id,
        role: selectedRole,
      });

      if (result.data.success) {
        setSuccess(
          `Ruolo ${selectedRole} assegnato con successo a ${selectedUser.displayName}`,
        );

        // Aggiorna lo stato dell'utente selezionato con i nuovi valori
        const updatedAdmin = selectedRole === "admin";
        const updatedResTecnico =
          selectedRole === "admin" || selectedRole === "resTecnico";

        const updatedUser = {
          ...selectedUser,
          role: selectedRole,
          admin: updatedAdmin,
          resTecnico: updatedResTecnico,
          customClaims: {
            ...(selectedUser.customClaims || {}),
            admin: updatedAdmin,
            resTecnico: updatedResTecnico,
          },
        };

        setSelectedUser(updatedUser);

        // Forza il refresh del token per ottenere i claims aggiornati
        if (autenticazione.currentUser) {
          try {
            await autenticazione.currentUser.getIdToken(true);
            console.log("Token aggiornato dopo assegnazione ruolo");
          } catch (error) {
            console.error("Errore durante il refresh del token:", error);
          }
        }
      } else {
        setError(
          result.data.message ||
            "Si è verificato un errore durante l'assegnazione del ruolo",
        );
      }
    } catch (err) {
      console.error("Errore durante l'assegnazione del ruolo:", err);
      setError(
        err.message ||
          "Si è verificato un errore durante l'assegnazione del ruolo",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Gestisce la creazione di un nuovo utente
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);

    if (!newUserEmail || !newUserPassword) {
      setCreateError("Email e password sono obbligatorie");
      return;
    }

    setIsCreating(true);

    try {
      const adminCreateUser = httpsCallable(functions, "adminCreateUser");
      const result = await adminCreateUser({
        email: newUserEmail,
        password: newUserPassword,
        displayName: newUserDisplayName,
        role: newUserRole,
      });

      setCreateSuccess(
        result.data.message || `Utente ${newUserEmail} creato con successo`,
      );
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserDisplayName("");
      setNewUserRole("basic");

      // Reindirizza l'admin alla pagina anagrafica del nuovo utente
      if (result.data.uid) {
        navigate(`/anagraficaDipendente/${result.data.uid}`);
      }
    } catch (err) {
      console.error("Errore durante la creazione dell'utente:", err);
      setCreateError(
        err.message || "Si e verificato un errore durante la creazione",
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Determina il ruolo attuale dell'utente selezionato
  const getCurrentRole = (user) => {
    if (!user) return "Non selezionato";

    if (user.role) {
      if (user.role === "admin") return "Admin";
      if (user.role === "resTecnico") return "Responsabile Tecnico";
      if (user.role === "basic") return "Utente Base";
    }

    // Controlla i customClaims
    if (user.customClaims?.admin === true) {
      return "Admin";
    }

    if (user.customClaims?.resTecnico === true) {
      return "Responsabile Tecnico";
    }

    // Controlla i campi diretti
    if (user.admin === true) {
      return "Admin";
    }

    if (user.resTecnico === true) {
      return "Responsabile Tecnico";
    }

    return "Utente Base";
  };

  return (
    <div className="gestione-ruoli-container">
      <h1>Gestione Ruoli Utente</h1>

      {isAdmin === false ? (
        <div className="error-container">
          <h2 className="error-message">Accesso Negato</h2>
          <p>Devi essere un amministratore per accedere a questa pagina.</p>
          <button className="back-btn" onClick={() => navigate("/")}>
            Torna alla Home
          </button>
        </div>
      ) : (
        <>
          <div className="top-actions">
            <button
              className="roles-example-btn"
              onClick={() => navigate("/esempio-ruoli")}
            >
              📘 Esempi di ruoli
            </button>
          </div>

          <div className="create-user-section">
            <h2>Crea Nuovo Utente</h2>
            <form className="create-user-form" onSubmit={handleCreateUser}>
              <div className="form-group">
                <label htmlFor="new-user-name">Nome utente:</label>
                <input
                  id="new-user-name"
                  type="text"
                  value={newUserDisplayName}
                  onChange={(e) => setNewUserDisplayName(e.target.value)}
                  disabled={isCreating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-email">Email:</label>
                <input
                  id="new-user-email"
                  type="email"
                  required
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  disabled={isCreating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-password">Password:</label>
                <input
                  id="new-user-password"
                  type="password"
                  required
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  disabled={isCreating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-role">Ruolo:</label>
                <select
                  id="new-user-role"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  disabled={isCreating}
                >
                  <option value="basic">Utente Base</option>
                  <option value="resTecnico">Responsabile Tecnico</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              <button
                type="submit"
                className="assign-role-btn"
                disabled={isCreating}
              >
                {isCreating ? "Creazione in corso..." : "Crea Utente"}
              </button>
              {createError && <p className="error-message">{createError}</p>}
              {createSuccess && (
                <p className="success-message">{createSuccess}</p>
              )}
            </form>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Cerca utente per nome o email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="gestione-ruoli-content">
            <div className="users-list">
              <h2>Utenti</h2>
              {filteredUsers && filteredUsers.length > 0 ? (
                <ul>
                  {filteredUsers.map((user) => (
                    <li
                      key={user.id || "unknown-id"}
                      className={
                        selectedUser && selectedUser.id === user.id
                          ? "selected"
                          : ""
                      }
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="user-info">
                        <span className="user-name">{user.displayName}</span>
                        <span className="user-email">{user.email}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-results">Nessun utente trovato</p>
              )}
            </div>

            <div className="role-assignment">
              <h2>Assegnazione Ruolo</h2>

              {selectedUser ? (
                <div className="selected-user-info">
                  <h3>{selectedUser.displayName}</h3>
                  <p className="user-email">{selectedUser.email}</p>
                  <p className="current-role">
                    Ruolo attuale: <span>{getCurrentRole(selectedUser)}</span>
                  </p>

                  <div className="role-selection">
                    <label htmlFor="role">Nuovo ruolo:</label>
                    <select
                      id="role"
                      value={selectedRole}
                      onChange={handleRoleChange}
                      disabled={isLoading}
                    >
                      <option value="">Seleziona un ruolo</option>
                      <option value="admin">Amministratore</option>
                      <option value="resTecnico">Responsabile Tecnico</option>
                      <option value="basic">Utente Base</option>
                    </select>
                  </div>

                  <button
                    className="assign-role-btn"
                    onClick={handleAssignRole}
                    disabled={isLoading || !selectedRole}
                  >
                    {isLoading ? "Assegnazione in corso..." : "Assegna Ruolo"}
                  </button>

                  {error && <p className="error-message">{error}</p>}
                  {success && <p className="success-message">{success}</p>}
                </div>
              ) : (
                <p className="select-user-prompt">
                  Seleziona un utente dalla lista
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GestioneRuoli;
